import { useState, useEffect } from "react";
import {
  areItemsValid,
  createCartWithItems,
  updateAppropriateCartPriceContext,
} from "~/use-cases/crystallize/read/cart";
import { Cart, getCart } from "~/actions/get-cart";
import { sendClientEvent } from "~/ui/components/aplia/Klaviyo/lib/klaviyoActions";
import { setCartCustomer } from "~/actions/add-cart";
import { useAuth } from "~/ui/hooks/useAuth";
import { Customer } from "@crystallize/schema/shop";

const STORAGE_KEY = "cart_items";

let sharedCartItems: { sku: string; quantity: number }[] = [];
let sharedIsLoading = false;
let sharedHasLoaded = false;
let sharedQuantity = 0;

let sharedCryzstallizeCart: Cart | null = null;
let sharedCachedCryzstallizeCartHash: string | null = null;
let sharedIsLoadingCryzstallizeCart = false;
let numberOfLoadingCryzstallizeCarts = 0;

let listeners = new Set<() => void>();

let userInfos: Partial<Customer> | null = null;
let voucherCodes: string[] = [];

function notify() {
  listeners.forEach((fn) => fn());
}

type LocalStorage = Partial<{
  items: { sku: string; quantity: number }[];
}>;

const isClient = typeof window !== "undefined";

function readStorage(key: string): string | null {
  if (!isClient) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  if (!isClient) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Private Mode / quota — keep in-memory cart */
  }
}

// Load from local storage
function loadFromStorage() {
  if (sharedHasLoaded || !isClient) return;
  try {
    const stored = readStorage(STORAGE_KEY);
    const data: LocalStorage | null = stored ? JSON.parse(stored) : null;
    if (data?.items) updateItems(data.items);
  } catch {
    /* corrupt JSON or storage blocked */
  }

  sharedHasLoaded = true;
  notify();
}

// Save to local storage
function saveToStorage(data: LocalStorage) {
  if (!isClient) return;
  try {
    const stored = readStorage(STORAGE_KEY);
    const oldData: LocalStorage | {} = stored ? JSON.parse(stored) : {};

    const filteredNewData: any = {};

    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) filteredNewData[key] = value;
    }

    writeStorage(
      STORAGE_KEY,
      JSON.stringify({ ...oldData, ...filteredNewData }),
    );
  } catch {
    /* Private Mode / quota */
  }
}

async function updateItems(items: { sku: string; quantity: number }[]) {
  sharedCartItems = items;
  sharedQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  notify();

  saveToStorage({ items });
  return await createCryzstallizeCart();
}

async function createCryzstallizeCart(ignoreDuplicate = false) {
  if (sharedCartItems.length === 0) return null;

  const hash = JSON.stringify(sharedCartItems);
  if (
    !!sharedCryzstallizeCart &&
    hash === sharedCachedCryzstallizeCartHash &&
    !ignoreDuplicate
  )
    return sharedCryzstallizeCart;

  sharedCachedCryzstallizeCartHash = hash;
  sharedIsLoadingCryzstallizeCart = true;
  numberOfLoadingCryzstallizeCarts += 1;
  notify();

  const cartId = await createCartWithItems(sharedCartItems);
  const shouldUpdatePriceContext =
    !!userInfos || voucherCodes.length > 0;
  if (userInfos) {
    await setCartCustomer(cartId, userInfos);
  }
  if (shouldUpdatePriceContext) {
    await updateAppropriateCartPriceContext(cartId, voucherCodes);
  }
  sharedCryzstallizeCart = await getCart(cartId);

  numberOfLoadingCryzstallizeCarts -= 1;
  if (numberOfLoadingCryzstallizeCarts === 0)
    sharedIsLoadingCryzstallizeCart = false;
  notify();

  return sharedCryzstallizeCart;
}

async function add(
  sku: string,
  quantity: number,
  email?: string,
): Promise<{ success: true } | { success: false; reason: string }> {
  // ── Optimistic update: apply to UI immediately ──────────────────────
  const existing = sharedCartItems.find((item) => item.sku === sku);
  const optimisticItems = existing
    ? sharedCartItems.map((item) =>
        item.sku === sku
          ? { ...item, quantity: item.quantity + quantity }
          : item,
      )
    : [...sharedCartItems, { sku, quantity }];

  const previousItems = sharedCartItems;
  sharedCartItems = optimisticItems;
  sharedQuantity = optimisticItems.reduce((sum, item) => sum + item.quantity, 0);
  notify();

  // ── Background validation + sync ────────────────────────────────────
  try {
    const valid = await areItemsValid([...previousItems, { sku, quantity }]);

    if (valid.valid) {
      updateItems(optimisticItems).then((cart) => {
        if (email) {
          const item = cart?.cartItems.find((i) => i.variant.sku === sku);
          if (!item) return;
          sendClientEvent({
            event: "added-to-cart",
            data: {
              email,
              productId: sku,
              productName: item.name,
              sku,
              quantity,
              price: item.price?.gross || 0,
            },
          });
        }
      });
      return { success: true };
    } else {
      // Validation failed – roll back the optimistic update
      sharedCartItems = previousItems;
      sharedQuantity = previousItems.reduce((sum, item) => sum + item.quantity, 0);
      notify();
      return { success: false, reason: valid.reason ?? "invalid" };
    }
  } catch {
    // Network error – roll back
    sharedCartItems = previousItems;
    sharedQuantity = previousItems.reduce((sum, item) => sum + item.quantity, 0);
    notify();
    return { success: false, reason: "network_error" };
  }
}

async function remove(sku: string, quantity: number, email?: string) {
  const existing = sharedCartItems.find((item) => item.sku === sku);
  if (!existing) return;

  // Get cart item details before removal for Klaviyo tracking
  if (email && sharedCryzstallizeCart) {
    const item = sharedCryzstallizeCart.cartItems.find((i) => i.variant.sku === sku);
    if (item) {
      sendClientEvent({
        event: "removed-from-cart",
        data: {
          email: email,
          productId: sku,
          productName: item.name,
          sku: sku,
          quantity: quantity,
          price: item.price?.gross || 0,
        },
      });
    }
  }

  const updatedQuantity = existing.quantity - quantity;
  const updated =
    updatedQuantity > 0
      ? sharedCartItems.map((item) =>
          item.sku === sku ? { ...item, quantity: updatedQuantity } : item,
        )
      : sharedCartItems.filter((item) => item.sku !== sku);

  await updateItems(updated);
}

function clear() {
  updateItems([]);
}

async function setVoucherCodes(codes: string[]) {
  voucherCodes = codes;
  notify();
  return await createCryzstallizeCart(true);
}

export function useCart() {
  const [, rerender] = useState({});
  const { userInfos: userInfosState, isAuthenticated } = useAuth()

  useEffect(() => {
    const listener = () => rerender({});
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) userInfos = userInfosState;
    else userInfos = null;
  }, [userInfosState, isAuthenticated]);

  useEffect(() => {
    loadFromStorage();
  }, []);

  return {
    items: sharedCartItems,
    quantity: sharedQuantity,
    isLoading: sharedIsLoading,
    crystallizeCart: sharedCryzstallizeCart,
    isLoadingCryzstallizeCart: sharedIsLoadingCryzstallizeCart,
    cloneCart: () => createCryzstallizeCart(true),
    hasLoaded: sharedHasLoaded,
    hash: sharedCachedCryzstallizeCartHash,
    voucherCodes,

    add,
    remove,
    clear,
    setVoucherCodes,
  };
}
